import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BulletinBoardModal } from './BulletinBoardModal.jsx';
import { BulletinBoardNote } from './BulletinBoardNote.jsx';

const PAGE_SIZE = 6;
function sortedPosts(posts) {
  return [...posts].sort((a, b) => Number(Boolean(b.pinned)) - Number(Boolean(a.pinned)) || new Date(b.publishAt || b.createdAt || 0) - new Date(a.publishAt || a.createdAt || 0));
}

export function BulletinBoardScene({ board, busy, onBack }) {
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState(null);
  const [imageFailed, setImageFailed] = useState(false);
  const selectedButton = useRef(null);
  const posts = useMemo(() => sortedPosts(Array.isArray(board.posts) ? board.posts : []), [board.posts]);
  const pages = Math.max(1, Math.ceil(posts.length / PAGE_SIZE));
  const visible = posts.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);
  useEffect(() => setPage((current) => Math.min(current, pages - 1)), [pages]);
  const closeNote = useCallback(() => { setSelected(null); queueMicrotask(() => selectedButton.current?.focus()); }, []);
  const openNote = useCallback((post) => { selectedButton.current = document.activeElement; setSelected(post); }, []);

  useEffect(() => {
    const previous = window.history.state;
    window.history.pushState({ ...(previous || {}), dopeManBoardScene: true }, '', window.location.href);
    let popped = false;
    const handlePop = (event) => {
      if (event.state?.dopeManBoardScene) return;
      popped = true;
      onBack();
    };
    window.addEventListener('popstate', handlePop);
    return () => {
      window.removeEventListener('popstate', handlePop);
      if (!popped && window.history.state?.dopeManBoardScene) window.history.replaceState(previous, '', window.location.href);
    };
  }, [onBack]);

  return (
    <div className="bulletin-board-overlay" role="dialog" aria-modal="true" aria-labelledby="bulletin-board-title">
      <header className="bulletin-board-toolbar">
        <button type="button" disabled={busy} onClick={onBack}>← Back to the store</button>
        <div><span>CORNER STORE COMMUNITY INFORMATION SYSTEM</span><h2 id="bulletin-board-title">{board.title || 'The Bulletin Board'}</h2></div>
        <span>{posts.length} LIVE NOTE{posts.length === 1 ? '' : 'S'}</span>
      </header>
      <div className={`bulletin-board-scroll ${imageFailed ? 'image-failed' : ''}`}>
        <div className="bulletin-board-canvas">
          {!imageFailed && <img className="bulletin-board-background" src="./scenes/corner-store-bulletin-board.webp" alt="A filthy community bulletin board outside The Corner Store" onError={() => setImageFailed(true)} />}
          <div className="bulletin-board-live-region" aria-label="Current bulletin-board posts">
            {visible.length ? visible.map((post) => <BulletinBoardNote key={post.id || post.reference || post.title} post={post} onOpen={openNote} />) : (
              <div className="bulletin-board-empty"><strong>NOTHING NEW</strong><span>Either the neighborhood is quiet or somebody stole the notices.</span></div>
            )}
          </div>
          <div className="bulletin-board-graffiti" aria-hidden="true">CAVEMAN OWES ME $12</div>
          {pages > 1 && <nav className="bulletin-board-pages" aria-label="Bulletin board pages">
            <button type="button" disabled={page === 0} onClick={() => setPage((value) => value - 1)}>← Newer</button>
            <span>{page + 1} / {pages}</span>
            <button type="button" disabled={page >= pages - 1} onClick={() => setPage((value) => value + 1)}>Older →</button>
          </nav>}
        </div>
      </div>
      <p className="bulletin-board-hint">Tap a note to read it. On a phone, swipe the board sideways to inspect the whole mess.</p>
      {selected && <BulletinBoardModal post={selected} onClose={closeNote} />}
    </div>
  );
}
